/**
 * `SecurityPanel` — the Security panel of the Account view (ADR-0146 §1).
 *
 * Magic-link-only auth means the security surface here is intentionally
 * narrow:
 *
 *   - Active sessions list with the current session tagged "this
 *     device". Each non-current row has a "Sign out" button that
 *     revokes that one session (ADR-0148, Slice 2 — `DELETE
 *     /v1/account/sessions/:session_id`, keyed on the public
 *     `session_id`, server-scoped to the caller's `user_id`). The
 *     current session has no per-row button — it can only be ended via
 *     sign-out (the server returns 409 if asked to revoke it here).
 *   - "Sign out everywhere else" button — bulk DELETE of every other
 *     session for this user (the server's `!= session_id_hash`
 *     predicate keeps the current session alive). Disabled when there's
 *     no other session.
 *   - Magic-link reauth note — copy explaining "you signed in with a
 *     magic link; to re-authenticate, sign out and request a new link."
 *
 * What is DELIBERATELY ABSENT:
 *   - No password change. There IS no password for any active user;
 *     the `password_hash` column is nullable and the magic-link signup
 *     path never writes it (ADR-0146 §"Security tab is wrong for
 *     magic-link-only auth").
 *   - No 2FA / authenticator / SMS. The auth model is magic-link only
 *     in v1; surfacing a 2FA toggle would lie about the system.
 */

import { Info } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import type { SessionWire } from '@sill/types/api';
import type { User } from '@sill/types';

import { patchMe } from '../../api/account-me.js';
import { useOptionalHttp } from '../../api/http-context.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { useToast } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import { formatLocation, formatUtcDateTime } from '../../lib/format.js';
import { Card, Pill, Tooltip } from '../../components/atoms/index.js';
import {
  getSessions,
  revokeOtherSessions,
  revokeSession,
} from '../../api/account-sessions.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; sessions: readonly SessionWire[] }
  | { kind: 'error'; message: string };

export function SecurityPanel(): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  // Optional — a few tests mount SecurityPanel without an AuthProvider.
  // Those paths render the sessions list without the geo-anomaly row.
  const auth = useOptionalAuth();
  const toast = useToast();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [revoking, setRevoking] = useState(false);
  // Which single session row is mid-revoke (its `session_id`), so only
  // that row's button shows the pending state. null = none in flight.
  const [revokingId, setRevokingId] = useState<string | null>(null);
  // Local mirror of the signed-in user's `session_geo_anomaly_email`
  // preference. Consolidated Notifications (2026-10-20) moved this
  // switch out of Account › Notifications into Account › Security —
  // this is a security event about THIS user's own sessions, not a
  // merchant notification.
  const signedInUser: User | null =
    auth?.state.kind === 'authenticated' ? auth.state.user : null;
  const geoDefaultOn =
    signedInUser?.notification_preferences?.['session_geo_anomaly_email'] !== false;
  const [geoChecked, setGeoChecked] = useState<boolean>(geoDefaultOn);
  const [geoPending, setGeoPending] = useState(false);
  useEffect(() => {
    setGeoChecked(
      signedInUser?.notification_preferences?.['session_geo_anomaly_email'] !== false,
    );
  }, [signedInUser]);
  const onToggleGeo = useCallback(async (): Promise<void> => {
    if (http === null || geoPending) return;
    const previous = geoChecked;
    const next = !previous;
    setGeoPending(true);
    setGeoChecked(next);
    const result = await patchMe(http, {
      notification_preferences: { session_geo_anomaly_email: next },
    });
    setGeoPending(false);
    switch (result.kind) {
      case 'ok': {
        const echoed =
          result.user.notification_preferences?.['session_geo_anomaly_email'];
        setGeoChecked(echoed !== false);
        toast({
          tone: 'lime',
          title: next
            ? 'Sign-in alerts turned on.'
            : 'Sign-in alerts turned off.',
        });
        void auth?.refresh();
        break;
      }
      case 'bad_request':
      case 'error':
        setGeoChecked(previous);
        toast({ tone: 'coral', title: 'Could not save preference.' });
        break;
      case 'rate_limited':
        setGeoChecked(previous);
        toast({ tone: 'amber', title: result.message });
        break;
    }
  }, [http, geoPending, geoChecked, toast, auth]);

  const reload = useCallback(async (): Promise<void> => {
    if (http === null) return;
    setState({ kind: 'loading' });
    const result = await getSessions(http);
    if (result.kind === 'ok') {
      setState({ kind: 'ready', sessions: result.sessions });
    } else {
      setState({ kind: 'error', message: result.message });
    }
  }, [http]);

  useEffect(() => {
    let cancelled = false;
    if (http === null) return;
    void (async () => {
      const result = await getSessions(http);
      if (cancelled) return;
      if (result.kind === 'ok') {
        setState({ kind: 'ready', sessions: result.sessions });
      } else {
        setState({ kind: 'error', message: result.message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [http]);

  const others_count =
    state.kind === 'ready'
      ? state.sessions.filter((s) => !s.is_current).length
      : 0;

  const onRevokeOthers = useCallback(async (): Promise<void> => {
    if (http === null || revoking || others_count === 0) return;
    setRevoking(true);
    const result = await revokeOtherSessions(http);
    setRevoking(false);
    switch (result.kind) {
      case 'ok':
        toast({
          tone: 'lime',
          title:
            result.revoked === 1
              ? '1 other session signed out.'
              : `${result.revoked} other sessions signed out.`,
        });
        await reload();
        break;
      case 'rate_limited':
        toast({ tone: 'amber', title: result.message });
        break;
      case 'error':
        toast({
          tone: 'coral',
          title: 'Could not sign out other sessions.',
          body: result.message,
        });
        break;
    }
  }, [http, revoking, others_count, toast, reload]);

  const onRevokeOne = useCallback(
    async (session_id: string): Promise<void> => {
      if (http === null || revokingId !== null) return;
      setRevokingId(session_id);
      const result = await revokeSession(http, session_id);
      setRevokingId(null);
      switch (result.kind) {
        case 'ok':
          toast({ tone: 'lime', title: 'Session signed out.' });
          await reload();
          break;
        case 'not_found':
          // Already gone — the list was stale. Treat as success and
          // refresh so the row disappears.
          toast({ tone: 'lime', title: 'Session already signed out.' });
          await reload();
          break;
        case 'current_session':
          // The per-row button is hidden for the current session, so
          // this is only reachable on a race. Explain rather than fail
          // silently.
          toast({
            tone: 'amber',
            title: 'That is your current session.',
            body: "Use 'Sign out everywhere else' or sign out from the menu.",
          });
          break;
        case 'rate_limited':
          toast({ tone: 'amber', title: result.message });
          break;
        case 'error':
          toast({
            tone: 'coral',
            title: 'Could not sign out that session.',
            body: result.message,
          });
          break;
      }
    },
    [http, revokingId, toast, reload],
  );

  return (
    <section id="security">
    <Card className="p-5 sm:p-6">
      <div
        className="mb-5 pb-4"
        style={{ borderBottom: `1px solid ${C.border}` }}
      >
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            marginBottom: 6,
          }}
        >
          SECURITY
        </div>
        <div
          style={{
            fontFamily: serifFont,
            fontSize: 22,
            color: C.text,
            letterSpacing: -0.5,
            marginBottom: 4,
          }}
        >
          Active sessions
        </div>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.muted,
            lineHeight: 1.5,
          }}
        >
          You signed in with a magic link. To re-authenticate, sign out
          and request a new link from the sign-in page.
        </div>
      </div>

      {state.kind === 'loading' ? (
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
            letterSpacing: 1,
          }}
        >
          Loading sessions…
        </div>
      ) : null}

      {state.kind === 'error' ? (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.coralFg,
            lineHeight: 1.5,
          }}
        >
          Couldn&apos;t load sessions — {state.message}
        </div>
      ) : null}

      {/* Consolidated Notifications 2026-10-20 — "Sign-in from a new
       *  country" lives here (own-session security), not under
       *  Account › Notifications (merchant-event emails). Hidden on
       *  auth-less test scaffolds (there is no user to attach the
       *  preference to). */}
      {signedInUser !== null ? (
      <div
        data-testid="session-geo-anomaly-row"
        className="flex items-start justify-between gap-3 p-3 mb-4"
        style={{ border: `1px solid ${C.border}`, borderRadius: 2 }}
      >
        <div
          className="flex-1"
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.text,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span>Sign-in from a new country</span>
          <Tooltip
            content={
              'An email when a session signs in from a country Sill '
                + 'has not seen on your account before. One email per '
                + 'new country.'
            }
            side="top"
          >
            <span
              aria-label="About sign-in alerts"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                color: C.muted,
                cursor: 'help',
              }}
            >
              <Info size={13} />
            </span>
          </Tooltip>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={geoChecked}
          aria-label="Sign-in from a new country"
          disabled={geoPending}
          onClick={() => void onToggleGeo()}
          style={{
            width: 44,
            height: 26,
            minWidth: 44,
            borderRadius: 13,
            border: `1px solid ${geoChecked ? C.lime : C.border}`,
            background: geoChecked ? C.lime : C.surface2,
            position: 'relative',
            cursor: geoPending ? 'not-allowed' : 'pointer',
            transition: 'background 120ms, border-color 120ms',
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: 2,
              left: geoChecked ? 20 : 2,
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: geoChecked ? C.ink : C.muted,
              transition: 'left 120ms',
            }}
          />
        </button>
      </div>
      ) : null}

      {state.kind === 'ready' ? (
        <div className="space-y-3">
          <div className="space-y-2">
            {state.sessions.map((session) => (
              <SessionRow
                key={session.session_id}
                session={session}
                C={C}
                onRevoke={onRevokeOne}
                revoking={revokingId === session.session_id}
                anyRevoking={revokingId !== null}
              />
            ))}
          </div>
          <div
            className="flex items-center justify-between gap-3 pt-3"
            style={{ borderTop: `1px solid ${C.borderSoft ?? C.border}` }}
          >
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.muted,
                lineHeight: 1.5,
              }}
            >
              {others_count === 0
                ? 'This is your only active session.'
                : others_count === 1
                  ? 'One other session is active. Sign it out below.'
                  : `${others_count} other sessions are active. Sign them all out below.`}
            </div>
            <button
              type="button"
              disabled={revoking || others_count === 0}
              onClick={() => void onRevokeOthers()}
              style={{
                fontFamily: monoFont,
                fontSize: 11,
                letterSpacing: 1,
                color:
                  revoking || others_count === 0 ? C.muted : C.coralFg,
                background: 'transparent',
                border: `1px solid ${
                  revoking || others_count === 0 ? C.border : C.coralFg
                }`,
                borderRadius: 2,
                padding: '8px 14px',
                minHeight: 36,
                cursor:
                  revoking || others_count === 0 ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {revoking ? 'SIGNING OUT…' : 'SIGN OUT EVERYWHERE ELSE'}
            </button>
          </div>
        </div>
      ) : null}
    </Card>
    </section>
  );
}

interface SessionRowProps {
  readonly session: SessionWire;
  readonly C: {
    text: string;
    muted: string;
    border: string;
    surface2: string;
    coralFg: string;
  };
  /** Revoke this row's session (non-current rows only). */
  readonly onRevoke: (session_id: string) => void;
  /** This row's own revoke is in flight. */
  readonly revoking: boolean;
  /** Some row (possibly another) is mid-revoke — disable to serialize. */
  readonly anyRevoking: boolean;
}

function SessionRow({
  session,
  C,
  onRevoke,
  revoking,
  anyRevoking,
}: SessionRowProps): JSX.Element {
  // Display values:
  //   - user-agent is bounded server-side (≤512 chars), but truncate
  //     defensively for the row anyway so a long UA doesn't blow the
  //     layout.
  // ADR-0150 — prefer the parsed device ("Chrome 120 · macOS") so a user can
  // recognize their own sessions; fall back to the raw UA if unparseable.
  const ua = session.created_user_agent ?? 'Unknown device';
  const deviceLabel = session.device
    ? `${session.device.browser} · ${session.device.os}`
    : ua.length > 80
      ? `${ua.slice(0, 77)}…`
      : ua;
  // ADR-0153 — show where the session is CURRENTLY used (last_seen_ip),
  // falling back to the sign-in IP for pre-migration rows.
  const ip = session.last_seen_ip ?? session.created_ip ?? 'unknown IP';
  // ADR-0150 Slice 2 — geo from the IP (empty when unavailable). Shared
  // "City, ST, Country" label — consistent with the activity trail.
  const locStr = formatLocation(session.location);
  return (
    <div
      className="flex items-center gap-3 p-3 flex-wrap"
      style={{
        background: C.surface2,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
      }}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <span
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              fontWeight: 500,
              wordBreak: 'break-word',
            }}
          >
            {deviceLabel}
          </span>
          {session.is_current ? <Pill tone="lime">THIS DEVICE</Pill> : null}
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            marginTop: 2,
            wordBreak: 'break-all',
          }}
        >
          {ip}
          {locStr !== '' ? ` · ${locStr}` : ''} · last seen{' '}
          {formatUtcDateTime(session.last_seen_at)} · expires{' '}
          {formatUtcDateTime(session.expires_at)}
        </div>
      </div>
      {/* Per-row revoke (ADR-0148). Hidden for the current session —
       *  that row can only be ended via sign-out, and the server
       *  returns 409 if asked here. While any row is mid-revoke the
       *  buttons disable to serialize the actions. */}
      {!session.is_current ? (
        <Tooltip content="Sign this session out.">
        <button
          type="button"
          disabled={anyRevoking}
          onClick={() => onRevoke(session.session_id)}
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            letterSpacing: 1,
            color: anyRevoking ? C.muted : C.coralFg,
            background: 'transparent',
            border: `1px solid ${anyRevoking ? C.border : C.coralFg}`,
            borderRadius: 2,
            padding: '6px 10px',
            minHeight: 30,
            cursor: anyRevoking ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {revoking ? 'SIGNING OUT…' : 'SIGN OUT'}
        </button>
        </Tooltip>
      ) : null}
    </div>
  );
}

